import { setRestedBonusElementVariant } from './rested-bonus-policy.js';
import { createNumberTicker } from './number-ticker.js';
import { createProductCardVisibilityController } from './product-card-visibility.js';

const PRODUCT_CARD_OPTION_SELECTOR = '[data-product-card-option]';
const initializedProductCards = new WeakSet();
const initializedProductCardPurchases = new WeakSet();

const syncPurchaseControls = (card, variant) => {
  const { id, available } = variant;
  const variantInput = card.querySelector('[data-product-card-add-id]');
  const addButton = card.querySelector('[data-product-card-add]');
  const addLabel = card.querySelector('[data-product-card-add-label]');
  const purchaseOptions = card.querySelector('[data-product-card-purchase-options]');
  const configured = purchaseOptions?.dataset.productCardActionLabel === 'Configure';
  const preorderChoice = variant.stoqPreorderOfferActive === true
    && Number(variant.inventoryQuantity) <= 0
    && Boolean(card.querySelector('[data-product-card-has-selling-plans]'));
  const needsChoice = configured || variant.requiresSellingPlan === true || preorderChoice;
  card.querySelector('.product-card__actions')?.classList.toggle('product-card__actions--choose-options', needsChoice && !configured);

  if (variantInput instanceof HTMLInputElement || variantInput instanceof HTMLSelectElement) {
    variantInput.value = String(id || '');
  }

  if (addButton instanceof HTMLButtonElement) {
    addButton.hidden = needsChoice;
    addButton.disabled = !available;
    addButton.setAttribute('aria-disabled', String(!available));
  }

  if (addLabel instanceof HTMLElement) {
    addLabel.textContent = available ? 'Add to bag' : 'Sold out';
  }

  if (purchaseOptions instanceof HTMLAnchorElement) {
    purchaseOptions.hidden = !needsChoice;
    const label = purchaseOptions.dataset.productCardActionLabel || 'Choose options';
    const title = purchaseOptions.dataset.productTitle || 'This product';
    const purchaseLabel = purchaseOptions.querySelector('[data-product-card-purchase-options-label]');
    purchaseOptions.setAttribute('aria-label', available ? `${label}: ${title}` : `${title} is sold out`);
    if (purchaseLabel) purchaseLabel.textContent = available ? label : 'Sold out';
    if (available) {
      purchaseOptions.removeAttribute('aria-disabled');
      purchaseOptions.removeAttribute('tabindex');
    } else {
      purchaseOptions.removeAttribute('href');
      purchaseOptions.setAttribute('aria-disabled', 'true');
      purchaseOptions.setAttribute('tabindex', '-1');
    }
  }
};

const toPricePresentation = (value) => {
  const text = String(value || '').trim();
  const match = text.match(/^(.*?)(\d[\d\s.,]*)(.*?)$/u);
  return match
    ? { prefix: match[1], number: match[2], suffix: match[3], text }
    : { number: text, text };
};

const canonicalRoastType = (value) => {
  const roastTypes = {
    filter: 'Filter',
    espresso: 'Espresso',
    omni: 'Omni',
    decaf: 'Decaf',
    blend: 'Blend',
  };

  return roastTypes[String(value || '').trim().toLowerCase()] || '';
};

const variantMatchesOption = (variant, option) => {
  const optionType = option.dataset.productCardOption;
  const optionValue = option.dataset.productCardOptionValue || '';

  if (optionType === 'roast-date') {
    return variant.roastDate === optionValue;
  }

  if (optionType === 'size') {
    const optionIndex = Number.parseInt(option.dataset.productCardOptionIndex || '', 10);
    return Number.isInteger(optionIndex) && variant.options?.[optionIndex] === optionValue;
  }

  return false;
};

const variantScore = (candidate, currentVariant, requestedOption) => {
  let score = candidate.roastDate && candidate.roastDate === currentVariant?.roastDate ? 8 : 0;
  const requestedIndex = requestedOption.dataset.productCardOption === 'size'
    ? Number.parseInt(requestedOption.dataset.productCardOptionIndex || '', 10)
    : -1;

  candidate.options?.forEach((value, index) => {
    if (index !== requestedIndex && value === currentVariant?.options?.[index]) {
      score += 2;
    }
  });

  return score;
};

const stockStateForVariant = (variant) => {
  const inventoryQuantity = Number(variant?.inventoryQuantity);
  const isLowStock = Boolean(
    variant?.available &&
    variant.inventoryManagement &&
    Number.isFinite(inventoryQuantity) &&
    inventoryQuantity > 0 &&
    inventoryQuantity <= 3
  );

  if (!variant?.available) {
    return { label: 'Sold out', className: '', visible: false };
  }

  if (isLowStock) {
    return { label: `Only ${inventoryQuantity} left!`, className: 'is-low-stock', visible: true };
  }

  return { label: '', className: '', visible: false };
};

const variantUrl = (card, variantId) => {
  const cardLink = card.querySelector('[data-product-card-link]');
  const url = new URL(
    card.dataset.productCardUrl || cardLink?.getAttribute('href') || window.location.href,
    window.location.origin,
  );
  url.searchParams.set('variant', String(variantId));
  return `${url.pathname}${url.search}${url.hash}`;
};

const syncCardLinks = (card, variantId) => {
  const href = variantUrl(card, variantId);

  card.querySelectorAll('[data-product-card-link]').forEach((link) => {
    if (link instanceof HTMLAnchorElement) {
      if (link.matches('[data-product-card-purchase-options][aria-disabled="true"]')) return;
      link.href = href;
    }
  });
};

const initializeProductCardPurchase = (purchase) => {
  if (!(purchase instanceof HTMLElement) || initializedProductCardPurchases.has(purchase)) {
    return;
  }

  const variantSelect = purchase.querySelector('[data-product-card-variant-select]');

  if (!(variantSelect instanceof HTMLSelectElement)) {
    return;
  }

  initializedProductCardPurchases.add(purchase);

  const card = purchase.closest('.product-card') || purchase;

  const syncSelectedVariant = () => {
    const selectedOption = variantSelect.selectedOptions[0];

    if (!(selectedOption instanceof HTMLOptionElement)) {
      return;
    }

    const available = selectedOption.dataset.productCardVariantAvailable !== 'false'
      && !selectedOption.disabled;
    const price = selectedOption.dataset.productCardVariantPrice;
    const priceElement = card.querySelector('[data-product-card-price]');

    syncPurchaseControls(card, {
      id: selectedOption.value,
      available,
      requiresSellingPlan: selectedOption.dataset.productCardRequiresSellingPlan === 'true',
      stoqPreorderOfferActive: selectedOption.dataset.productCardPreorderActive?.trim() === 'true',
      inventoryQuantity: Number(selectedOption.dataset.productCardInventoryQuantity),
    });
    syncCardLinks(card, selectedOption.value);

    if (price && priceElement instanceof HTMLElement) {
      priceElement.textContent = price;
    }
  };

  variantSelect.addEventListener('change', syncSelectedVariant);

  syncSelectedVariant();
};

const initializeProductCard = (card) => {
  if (!(card instanceof HTMLElement) || initializedProductCards.has(card)) {
    return;
  }

  const variantData = card.querySelector('[data-product-card-variants]');
  const optionButtons = [...card.querySelectorAll(PRODUCT_CARD_OPTION_SELECTOR)].filter(
    (option) => option instanceof HTMLButtonElement,
  );
  const price = card.querySelector('[data-product-card-price]');
  const priceText = card.querySelector('[data-product-card-price-text]');
  const stockPill = card.querySelector('[data-product-card-stock]');
  const readiness = card.querySelector('[data-product-card-readiness]');
  const publicBonus = card.querySelector('[data-loyalty-rested-bonus][data-rested-public-offer="true"]');
  const badge = card.querySelector('[data-product-card-badge]');
  const status = card.querySelector('[data-product-card-status]');

  if (!(variantData instanceof HTMLScriptElement)) {
    return;
  }

  let variants = [];

  try {
    variants = JSON.parse(variantData.textContent || '[]');
  } catch {
    return;
  }

  if (!Array.isArray(variants) || !variants.length) {
    return;
  }

  initializedProductCards.add(card);

  let selectedVariant = variants.find(
    (variant) => String(variant.id) === card.dataset.selectedVariantId,
  ) || variants.find((variant) => variant.available) || variants[0];

  const priceTicker = price instanceof HTMLElement ? createNumberTicker(price) : null;

  const renderVariant = (variant, announcement = '') => {
    selectedVariant = variant;
    card.dataset.selectedVariantId = String(variant.id);

    optionButtons.forEach((option) => {
      const available = variants.some(
        (candidate) => candidate.available && variantMatchesOption(candidate, option),
      );
      const selected = variantMatchesOption(variant, option);

      option.disabled = !available;
      option.classList.toggle('is-unavailable', !available);
      option.classList.toggle('is-selected', selected);
      option.setAttribute('aria-pressed', String(selected));
    });

    priceTicker?.set(toPricePresentation(variant.price), {
      animate: Boolean(announcement),
      numericValue: Number(variant.priceMinorUnits),
    });
    if (priceText instanceof HTMLElement) priceText.textContent = variant.price || '';

    if (publicBonus instanceof HTMLElement) {
      setRestedBonusElementVariant(publicBonus, variant);
    }
    const stockState = stockStateForVariant(variant);

    if (stockPill instanceof HTMLElement) {
      stockPill.classList.remove('is-low-stock', 'is-in-stock');
      stockPill.hidden = !stockState.visible;
      stockPill.textContent = stockState.visible ? stockState.label : '';

      if (stockState.className) {
        stockPill.classList.add(stockState.className);
      }
    }

    if (readiness instanceof HTMLElement) {
      readiness.hidden = !variant.readyNow || !variant.available;
      readiness.textContent = variant.readyNow ? 'Ready to brew' : '';
    }

    if (badge instanceof HTMLElement) {
      const roastType = canonicalRoastType(variant.roastType);
      badge.hidden = !roastType;
      badge.textContent = roastType;
    }

    syncPurchaseControls(card, variant);
    syncCardLinks(card, variant.id);

    if (announcement && status instanceof HTMLElement) {
      const isReadyNow = Boolean(variant.available && variant.readyNow === true);
      const readinessAnnouncement = variant.readyNow ? ' Ready to brew.' : '';
      const stockAnnouncement = stockState.label ? ` ${stockState.label}.` : '';
      status.textContent = `${announcement}. ${variant.price}.${stockAnnouncement}${isReadyNow ? readinessAnnouncement : ''}`;
    }
  };

  optionButtons.forEach((option) => {
    option.addEventListener('click', () => {
      const candidates = variants
        .filter((variant) => variant.available && variantMatchesOption(variant, option))
        .sort(
          (first, second) =>
            variantScore(second, selectedVariant, option) - variantScore(first, selectedVariant, option),
        );

      if (!candidates.length) {
        return;
      }

      renderVariant(candidates[0], `${option.textContent.trim()} selected`);
    });
  });

  renderVariant(selectedVariant);
};

const productCardVisibility = createProductCardVisibilityController(initializeProductCard);

const initializeProductCardsWithin = (root, { cleanup = true } = {}) => {
  const showcaseCards = [];
  const purchaseCards = [];

  if (root instanceof Element && root.matches('[data-product-card-showcase]')) {
    showcaseCards.push(root);
  }

  if (root instanceof Element && root.matches('[data-product-card-purchase]')) {
    purchaseCards.push(root);
  }

  root.querySelectorAll?.('[data-product-card-showcase]').forEach((card) => showcaseCards.push(card));
  root.querySelectorAll?.('[data-product-card-purchase]').forEach((card) => purchaseCards.push(card));
  showcaseCards.forEach(productCardVisibility.initialize);
  purchaseCards.forEach(initializeProductCardPurchase);
  if (cleanup) productCardVisibility.cleanup();
};

initializeProductCardsWithin(document);

document.addEventListener('sigma:collection-view:updated', (event) => {
  if (event.detail?.appendOnly && Array.isArray(event.detail.cards)) {
    event.detail.cards.forEach((card) => initializeProductCardsWithin(card, { cleanup: false }));
    productCardVisibility.cleanup();
    return;
  }
  initializeProductCardsWithin(event.detail?.view || event.target);
});

document.addEventListener('shopify:section:load', (event) => {
  initializeProductCardsWithin(event.target);
});

document.addEventListener('submit', (event) => {
  const form = event.target;

  if (
    form instanceof HTMLFormElement
    && form.querySelector('[data-product-card-add]')
    && form.hasAttribute('aria-busy')
  ) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
}, { capture: true });

// Run before each surface's mode handler. Batch geometry reads before writes,
// and let CSS retarget an interrupted transition from its current position.
const cardMotionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
const movingCards = new Set();
let cardMotionTimer = 0;
const finishCardModeMotion = () => {
  window.clearTimeout(cardMotionTimer);
  movingCards.forEach((card) => {
    card.removeAttribute('data-product-card-motion');
    if (card.dataset.productCardMode === 'advanced') {
      card.dataset.productCardProfileFill = 'on';
    }
  });
  movingCards.clear();
};

document.addEventListener('click', (event) => {
  const control = event.target instanceof Element && event.target.closest(
    '[data-collection-mode-button], [data-product-mode-button], .coffee-finder__modes [data-mode]',
  );
  if (!control || control.getAttribute('aria-pressed') === 'true') return;

  if (cardMotionPreference.matches || event.detail === 0) {
    finishCardModeMotion();
    return;
  }

  const visibleCards = new Set(
    [...document.querySelectorAll('[data-product-card-showcase]')].filter((card) => {
      const rect = card.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight
        && rect.right > 0 && rect.left < window.innerWidth;
    }),
  );

  window.clearTimeout(cardMotionTimer);
  movingCards.forEach((card) => {
    if (!visibleCards.has(card)) {
      card.removeAttribute('data-product-card-motion');
      movingCards.delete(card);
    }
  });
  visibleCards.forEach((card) => {
    card.setAttribute('data-product-card-motion', '');
    movingCards.add(card);
  });
  // Covers the 220ms reflow and the two-frame, 180ms meter fill.
  cardMotionTimer = window.setTimeout(finishCardModeMotion, 300);
}, { capture: true });

cardMotionPreference.addEventListener('change', finishCardModeMotion);
window.addEventListener('resize', finishCardModeMotion, { passive: true });
document.addEventListener('visibilitychange', () => {
  if (document.hidden) finishCardModeMotion();
});
